import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { authFetch } from '../../lib/authFetch';

const GOLD = '#C9A84C';

// ─── Timezones ──────────────────────────────────────────────────────────────
const TIMEZONES = [
  { group: 'North America', zones: [
    { label: 'Pacific Time (PT)',   value: 'America/Los_Angeles' },
    { label: 'Mountain Time (MT)',  value: 'America/Denver' },
    { label: 'Central Time (CT)',   value: 'America/Chicago' },
    { label: 'Eastern Time (ET)',   value: 'America/New_York' },
    { label: 'Atlantic Time (AT)',  value: 'America/Halifax' },
    { label: 'Newfoundland (NT)',   value: 'America/St_Johns' },
    { label: 'Alaska (AKT)',        value: 'America/Anchorage' },
    { label: 'Hawaii (HST)',        value: 'Pacific/Honolulu' },
    { label: 'Phoenix (AZ)',        value: 'America/Phoenix' },
    { label: 'Vancouver (PT)',      value: 'America/Vancouver' },
    { label: 'Edmonton (MT)',       value: 'America/Edmonton' },
    { label: 'Winnipeg (CT)',       value: 'America/Winnipeg' },
    { label: 'Toronto (ET)',        value: 'America/Toronto' },
  ]},
  { group: 'South America', zones: [
    { label: 'São Paulo (BRT)',     value: 'America/Sao_Paulo' },
    { label: 'Buenos Aires',        value: 'America/Argentina/Buenos_Aires' },
    { label: 'Bogotá (COT)',        value: 'America/Bogota' },
    { label: 'Lima (PET)',          value: 'America/Lima' },
    { label: 'Santiago (CLT)',      value: 'America/Santiago' },
  ]},
  { group: 'Europe', zones: [
    { label: 'UTC / GMT',           value: 'UTC' },
    { label: 'London (GMT/BST)',    value: 'Europe/London' },
    { label: 'Dublin (IST)',        value: 'Europe/Dublin' },
    { label: 'Paris (CET)',         value: 'Europe/Paris' },
    { label: 'Berlin (CET)',        value: 'Europe/Berlin' },
    { label: 'Amsterdam (CET)',     value: 'Europe/Amsterdam' },
    { label: 'Madrid (CET)',        value: 'Europe/Madrid' },
    { label: 'Rome (CET)',          value: 'Europe/Rome' },
    { label: 'Stockholm (CET)',     value: 'Europe/Stockholm' },
    { label: 'Warsaw (CET)',        value: 'Europe/Warsaw' },
    { label: 'Athens (EET)',        value: 'Europe/Athens' },
    { label: 'Helsinki (EET)',      value: 'Europe/Helsinki' },
    { label: 'Kyiv (EET)',          value: 'Europe/Kyiv' },
    { label: 'Istanbul (TRT)',      value: 'Europe/Istanbul' },
    { label: 'Moscow (MSK)',        value: 'Europe/Moscow' },
  ]},
  { group: 'Africa & Middle East', zones: [
    { label: 'Cairo (EET)',         value: 'Africa/Cairo' },
    { label: 'Lagos (WAT)',         value: 'Africa/Lagos' },
    { label: 'Nairobi (EAT)',       value: 'Africa/Nairobi' },
    { label: 'Johannesburg (SAST)', value: 'Africa/Johannesburg' },
    { label: 'Dubai (GST)',         value: 'Asia/Dubai' },
    { label: 'Riyadh (AST)',        value: 'Asia/Riyadh' },
    { label: 'Tehran (IRST)',       value: 'Asia/Tehran' },
  ]},
  { group: 'Asia & Pacific', zones: [
    { label: 'Karachi (PKT)',       value: 'Asia/Karachi' },
    { label: 'Kolkata (IST)',       value: 'Asia/Kolkata' },
    { label: 'Colombo (SLST)',      value: 'Asia/Colombo' },
    { label: 'Dhaka (BST)',         value: 'Asia/Dhaka' },
    { label: 'Bangkok (ICT)',       value: 'Asia/Bangkok' },
    { label: 'Singapore (SGT)',     value: 'Asia/Singapore' },
    { label: 'Kuala Lumpur (MYT)', value: 'Asia/Kuala_Lumpur' },
    { label: 'Jakarta (WIB)',       value: 'Asia/Jakarta' },
    { label: 'Hong Kong (HKT)',     value: 'Asia/Hong_Kong' },
    { label: 'Shanghai (CST)',      value: 'Asia/Shanghai' },
    { label: 'Taipei (CST)',        value: 'Asia/Taipei' },
    { label: 'Seoul (KST)',         value: 'Asia/Seoul' },
    { label: 'Tokyo (JST)',         value: 'Asia/Tokyo' },
    { label: 'Sydney (AEST)',       value: 'Australia/Sydney' },
    { label: 'Melbourne (AEST)',    value: 'Australia/Melbourne' },
    { label: 'Brisbane (AEST)',     value: 'Australia/Brisbane' },
    { label: 'Perth (AWST)',        value: 'Australia/Perth' },
    { label: 'Auckland (NZST)',     value: 'Pacific/Auckland' },
  ]},
];

const ALL_ZONES = TIMEZONES.flatMap(g => g.zones);

// ─── Helpers ─────────────────────────────────────────────────────────────────
function getLocalNow(tz) {
  return new Date(new Date().toLocaleString('en-US', { timeZone: tz }));
}

function fmtTime(tz) {
  return new Date().toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true });
}

function fmtDate(tz) {
  return new Date().toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' });
}


function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function startOfDay(d) {
  const copy = new Date(d); copy.setHours(0,0,0,0); return copy;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function toInputDatetime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromInputDatetime(val) {
  if (!val) return '';
  return new Date(val).toISOString();
}

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const EVENT_COLORS = [
  { label: 'Gold',    value: '#C9A84C' },
  { label: 'Blue',    value: '#3B82F6' },
  { label: 'Green',   value: '#22C55E' },
  { label: 'Red',     value: '#EF4444' },
  { label: 'Purple',  value: '#A855F7' },
  { label: 'Pink',    value: '#EC4899' },
  { label: 'Orange',  value: '#F97316' },
  { label: 'Teal',    value: '#14B8A6' },
];

// ─── Timezone Selector ────────────────────────────────────────────────────────
function TimezoneSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const label = ALL_ZONES.find(z => z.value === value)?.label || value;

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = search.trim()
    ? ALL_ZONES.filter(z => z.label.toLowerCase().includes(search.toLowerCase()) || z.value.toLowerCase().includes(search.toLowerCase()))
    : null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(o => !o); setSearch(''); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8, padding: '5px 10px', cursor: 'pointer', color: 'rgba(255,255,255,0.8)',
          fontSize: 12, fontWeight: 600, transition: 'all 150ms',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
        {label}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 200,
          background: 'rgba(14,14,22,0.98)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          width: 260, maxHeight: 340, display: 'flex', flexDirection: 'column',
          backdropFilter: 'blur(20px)',
        }}>
          <div style={{ padding: '8px 8px 4px' }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search timezone…"
              style={{
                width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 6, padding: '6px 10px', color: 'rgba(255,255,255,0.85)',
                fontSize: 12, outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1, paddingBottom: 6 }}>
            {filtered ? (
              filtered.map(z => (
                <TzOption key={z.value} zone={z} selected={value === z.value} onSelect={() => { onChange(z.value); setOpen(false); }} />
              ))
            ) : (
              TIMEZONES.map(group => (
                <div key={group.group}>
                  <div style={{ padding: '6px 12px 2px', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {group.group}
                  </div>
                  {group.zones.map(z => (
                    <TzOption key={z.value} zone={z} selected={value === z.value} onSelect={() => { onChange(z.value); setOpen(false); }} />
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TzOption({ zone, selected, onSelect }) {
  return (
    <button
      onClick={onSelect}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '5px 12px', background: selected ? `${GOLD}18` : 'transparent',
        border: 'none', cursor: 'pointer', color: selected ? GOLD : 'rgba(255,255,255,0.75)',
        fontSize: 12, textAlign: 'left', transition: 'background 100ms',
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
    >
      <span>{zone.label}</span>
      {selected && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>}
    </button>
  );
}

// ─── Event Modal (draggable, portal-rendered) ─────────────────────────────────
function EventModal({ event, defaultDate, timezone, onSave, onDelete, onClose, googleConnected }) {
  const isEdit = !!event?.id;
  const defaultStart = defaultDate
    ? (() => { const d = new Date(defaultDate); d.setHours(9, 0, 0, 0); return d.toISOString(); })()
    : new Date().toISOString();
  const defaultEnd = defaultDate
    ? (() => { const d = new Date(defaultDate); d.setHours(10, 0, 0, 0); return d.toISOString(); })()
    : new Date(Date.now() + 3600000).toISOString();

  const [form, setForm] = useState({
    title:       event?.title       || '',
    description: event?.description || '',
    location:    event?.location    || '',
    start_time:  event?.start_time  || defaultStart,
    end_time:    event?.end_time    || defaultEnd,
    all_day:     event?.all_day     || false,
    color:       event?.color       || '#C9A84C',
  });
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addToGoogle, setAddToGoogle] = useState(false);

  // ── Drag state ─────────────────────────────────────────────────────────────
  const [pos, setPos]     = useState(null);
  const dragging          = useRef(false);
  const dragOrigin        = useRef({});

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const dx = e.clientX - dragOrigin.current.mx;
      const dy = e.clientY - dragOrigin.current.my;
      const TASKBAR_H = 58;
      const newTop  = Math.max(0, Math.min(window.innerHeight - TASKBAR_H - 100, dragOrigin.current.oy + dy));
      const newLeft = Math.max(0, Math.min(window.innerWidth - 100, dragOrigin.current.ox + dx));
      setPos({ top: newTop, left: newLeft });
    };
    const onUp = () => { dragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const onHeaderMouseDown = (e) => {
    const win = e.currentTarget.closest('[data-cal-window]');
    if (!win) return;
    const rect = win.getBoundingClientRect();
    dragging.current = true;
    dragOrigin.current = { mx: e.clientX, my: e.clientY, ox: rect.left, oy: rect.top };
    e.preventDefault();
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    await onSave(form, isEdit ? event.id : null, addToGoogle && googleConnected);
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(event.id);
    setDeleting(false);
  };

  const windowStyle = pos
    ? { position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }
    : {
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -60%)',
        zIndex: 9999,
      };

  const modal = (
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}
        onClick={onClose}
      />
      <div
        data-cal-window
        style={{
          ...windowStyle,
          background: 'rgba(14,14,22,0.98)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
          width: 420, maxWidth: 'calc(100vw - 32px)', overflow: 'hidden',
          animation: pos ? 'none' : 'cal-modal-in 180ms ease-out both',
          userSelect: dragging.current ? 'none' : 'auto',
        }}
      >
        <style>{`@keyframes cal-modal-in { from { opacity:0; transform:translate(-50%,-58%) scale(0.96); } to { opacity:1; transform:translate(-50%,-60%) scale(1); } }`}</style>

        {/* Header – drag handle */}
        <div
          onMouseDown={onHeaderMouseDown}
          style={{
            padding: '14px 20px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'grab', background: 'rgba(255,255,255,0.02)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="12" height="12" viewBox="0 0 16 16" fill="rgba(255,255,255,0.2)">
              <circle cx="4" cy="4" r="1.5"/><circle cx="12" cy="4" r="1.5"/>
              <circle cx="4" cy="8" r="1.5"/><circle cx="12" cy="8" r="1.5"/>
              <circle cx="4" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/>
            </svg>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.9)', margin: 0 }}>{isEdit ? 'Edit Event' : 'New Event'}</h3>
          </div>
          <button
            onClick={onClose}
            onMouseDown={e => e.stopPropagation()}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 4px' }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            autoFocus
            value={form.title}
            onChange={e => set('title', e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
            placeholder="Event title"
            style={{
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, padding: '10px 12px', color: 'rgba(255,255,255,0.9)',
              fontSize: 15, fontWeight: 600, outline: 'none', width: '100%', boxSizing: 'border-box',
            }}
          />

          {/* All-day toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <div
              onClick={() => set('all_day', !form.all_day)}
              style={{
                width: 36, height: 20, borderRadius: 10, transition: 'background 200ms',
                background: form.all_day ? GOLD : 'rgba(255,255,255,0.15)', position: 'relative', cursor: 'pointer',
              }}
            >
              <div style={{ position: 'absolute', top: 2, left: form.all_day ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 200ms' }} />
            </div>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>All day</span>
          </label>

          {/* Date/Time inputs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 4 }}>Start</label>
              <input
                type={form.all_day ? 'date' : 'datetime-local'}
                value={form.all_day ? form.start_time.slice(0, 10) : toInputDatetime(form.start_time)}
                onChange={e => set('start_time', form.all_day ? `${e.target.value}T00:00:00Z` : fromInputDatetime(e.target.value))}
                style={dtInputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 4 }}>End</label>
              <input
                type={form.all_day ? 'date' : 'datetime-local'}
                value={form.all_day ? form.end_time.slice(0, 10) : toInputDatetime(form.end_time)}
                onChange={e => set('end_time', form.all_day ? `${e.target.value}T23:59:59Z` : fromInputDatetime(e.target.value))}
                style={dtInputStyle}
              />
            </div>
          </div>

          {/* Location */}
          <input
            value={form.location}
            onChange={e => set('location', e.target.value)}
            placeholder="📍 Location (optional)"
            style={{ ...dtInputStyle, fontSize: 12 }}
          />

          {/* Description */}
          <textarea
            value={form.description}
            onChange={e => set('description', e.target.value)}
            placeholder="Add a description…"
            rows={2}
            style={{ ...dtInputStyle, resize: 'vertical', fontSize: 12, fontFamily: 'inherit' }}
          />

          {/* Color picker */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Color</span>
            {EVENT_COLORS.map(c => (
              <button
                key={c.value}
                onClick={() => set('color', c.value)}
                title={c.label}
                style={{
                  width: 20, height: 20, borderRadius: '50%', background: c.value, border: 'none', cursor: 'pointer',
                  outline: form.color === c.value ? `2px solid ${c.value}` : 'none',
                  outlineOffset: 2, transition: 'outline 150ms',
                  boxShadow: form.color === c.value ? `0 0 0 1px rgba(255,255,255,0.3)` : 'none',
                }}
              />
            ))}
          </div>

          {/* Add to Google Calendar */}
          {googleConnected && !isEdit && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={addToGoogle} onChange={e => setAddToGoogle(e.target.checked)} style={{ accentColor: '#4285F4', width: 14, height: 14 }} />
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                <span style={{ color: '#4285F4', fontWeight: 600 }}>G</span> Also add to Google Calendar
              </span>
            </label>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            {isEdit && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.65)', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer' }}>
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !form.title.trim()}
              style={{ background: GOLD, border: 'none', color: '#000', borderRadius: 8, padding: '7px 16px', fontSize: 12, cursor: 'pointer', fontWeight: 700, opacity: saving || !form.title.trim() ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(modal, document.body);
}

const dtInputStyle = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8, padding: '8px 10px', color: 'rgba(255,255,255,0.8)',
  fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box',
  colorScheme: 'dark',
};

// ─── Calendar Panel ───────────────────────────────────────────────────────────
function CalendarPanel({ onClose, timezone, onTimezoneChange }) {
  const panelRef = useRef(null);
  const today = getLocalNow(timezone);

  const [currentMonth, setCurrentMonth] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [selectedDay, setSelectedDay]   = useState(startOfDay(today));
  const [events, setEvents]             = useState([]);
  const [loading, setLoading]           = useState(false);
  const [showModal, setShowModal]       = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [syncing, setSyncing]           = useState(false);
  const [syncMsg, setSyncMsg]           = useState('');

  // ── Close on outside click ────────────────────────────────────────────────
  useEffect(() => {
    const h = e => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        const modal = document.querySelector('[data-cal-modal]');
        if (!modal) onClose();
      }
    };
    setTimeout(() => document.addEventListener('mousedown', h), 100);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  // ── Fetch events ──────────────────────────────────────────────────────────
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { year, month } = currentMonth;
      const start = new Date(year, month - 1, 1).toISOString();
      const end   = new Date(year, month + 2, 0, 23, 59, 59).toISOString();
      const res = await authFetch(`/api/calendar/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch { setEvents([]); }
    setLoading(false);
  }, [currentMonth]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // ── Fetch Google status ───────────────────────────────────────────────────
  useEffect(() => {
    authFetch('/api/calendar/google/status')
      .then(r => r.json())
      .then(d => setGoogleStatus({ connected: d.connected, lastSynced: d.last_synced_at, loading: false }))
      .catch(() => setGoogleStatus({ connected: false, loading: false }));
  }, []);

  // ── Google connect ────────────────────────────────────────────────────────
  const connectGoogle = async () => {
    try {
      const res = await authFetch('/api/calendar/google/auth-url');
      const { url, error } = await res.json();
      if (error) { setSyncMsg(error === 'Google Calendar not configured' ? 'Google Calendar sync is not yet enabled for this platform. An admin needs to add the Google Calendar API credentials.' : error); return; }
      const popup = window.open(url, 'gcal-auth', 'width=520,height=600,left=200,top=100');
      const handleMsg = (e) => {
        if (e.data?.type === 'gcal-auth') {
          window.removeEventListener('message', handleMsg);
          popup?.close();
          if (e.data.success) {
            setGoogleStatus(s => ({ ...s, connected: true }));
            setSyncMsg('Google Calendar connected! Syncing…');
            handleSync();
          } else {
            setSyncMsg(e.data.error || 'Connection failed');
          }
        }
      };
      window.addEventListener('message', handleMsg);
    } catch (err) {
      setSyncMsg('Failed to connect Google Calendar');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const res = await authFetch('/api/calendar/google/sync', { method: 'POST' });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSyncMsg(`Synced ${data.synced} events from Google`);
      await fetchEvents();
      setGoogleStatus(s => ({ ...s, lastSynced: new Date().toISOString() }));
    } catch (err) {
      setSyncMsg(`Sync failed: ${err.message}`);
    }
    setSyncing(false);
    setTimeout(() => setSyncMsg(''), 5000);
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google Calendar? Google events will stay in your local calendar.')) return;
    await authFetch('/api/calendar/google/disconnect', { method: 'DELETE' });
    setGoogleStatus({ connected: false, loading: false });
    setSyncMsg('Google Calendar disconnected');
    setTimeout(() => setSyncMsg(''), 3000);
  };

  // ── Event CRUD ────────────────────────────────────────────────────────────
  const saveEvent = async (form, id, alsoGoogle) => {
    try {
      const method = id ? 'PUT' : 'POST';
      const url = id ? `/api/calendar/events/${id}` : '/api/calendar/events';
      const res = await authFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      if (alsoGoogle && data.event?.id) {
        await authFetch('/api/calendar/google/create-event', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_id: data.event.id }) }).catch(() => {});
      }
      await fetchEvents();
      setShowModal(false);
      setEditingEvent(null);
    } catch (err) {
      console.error('[calendar] save error:', err.message);
    }
  };

  const deleteEvent = async (id) => {
    await authFetch(`/api/calendar/events/${id}`, { method: 'DELETE' });
    await fetchEvents();
    setShowModal(false);
    setEditingEvent(null);
  };

  // ── Calendar grid data ────────────────────────────────────────────────────
  const { year, month } = currentMonth;
  const daysInMonth  = getDaysInMonth(year, month);
  const firstDay     = getFirstDayOfMonth(year, month);
  const totalCells   = Math.ceil((firstDay + daysInMonth) / 7) * 7;

  const eventsByDay = {};
  events.forEach(ev => {
    const d = new Date(ev.start_time);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (!eventsByDay[key]) eventsByDay[key] = [];
    eventsByDay[key].push(ev);
  });

  const selectedDayEvents = events.filter(ev => isSameDay(new Date(ev.start_time), selectedDay)).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

  const prevMonth = () => setCurrentMonth(({ year, month }) => month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 });
  const nextMonth = () => setCurrentMonth(({ year, month }) => month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 });
  const goToday   = () => {
    const t = getLocalNow(timezone);
    setCurrentMonth({ year: t.getFullYear(), month: t.getMonth() });
    setSelectedDay(startOfDay(t));
  };

  return (
    <>
      <div
        ref={panelRef}
        style={{
          position: 'fixed', bottom: 58, right: 6, zIndex: 60,
          width: 'min(680px, calc(100vw - 20px))',
          maxHeight: 'calc(100vh - 80px)',
          background: 'rgba(12,12,20,0.97)',
          backdropFilter: 'blur(28px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 14,
          boxShadow: '0 -8px 40px rgba(0,0,0,0.7)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animation: 'cal-panel-in 200ms ease-out both',
        }}
      >
        <style>{`
          @keyframes cal-panel-in {
            from { opacity:0; transform:translateY(10px) scale(0.98); }
            to   { opacity:1; transform:translateY(0) scale(1); }
          }
          .cal-day:hover { background: rgba(255,255,255,0.05) !important; }
          .cal-day-today { background: rgba(201,168,76,0.1) !important; }
          .cal-ev-item:hover { background: rgba(255,255,255,0.07) !important; }
        `}</style>

        {/* ── Top bar ── */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <span style={{ fontSize: 16 }}>📅</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>Calendar</span>
          </div>
          <TimezoneSelector value={timezone} onChange={onTimezoneChange} />
          <button
            onClick={() => { setShowModal(true); setEditingEvent(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: GOLD, border: 'none', borderRadius: 8, padding: '6px 12px', color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
          >
            + New Event
          </button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px' }}>✕</button>
        </div>

        {/* ── Google Calendar banner ── */}
        {!googleStatus.loading && (
          <div style={{ padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.02)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {googleStatus.connected ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <span style={{ fontSize: 11, color: '#4285F4', fontWeight: 600 }}>Google Calendar connected</span>
                {googleStatus.lastSynced && (
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
                    Last synced {new Date(googleStatus.lastSynced).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                )}
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  style={{ background: 'rgba(66,133,244,0.15)', border: '1px solid rgba(66,133,244,0.3)', color: '#4285F4', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                >
                  {syncing ? 'Syncing…' : '↻ Sync'}
                </button>
                <button
                  onClick={disconnectGoogle}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 10, cursor: 'pointer', marginLeft: 'auto' }}
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Connect Google Calendar to sync your events</span>
                <button
                  onClick={connectGoogle}
                  style={{ background: 'rgba(66,133,244,0.15)', border: '1px solid rgba(66,133,244,0.35)', color: '#4285F4', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}
                >
                  Connect
                </button>
              </div>
            )}
          </div>
        )}

        {syncMsg && (
          <div style={{ padding: '6px 16px', background: 'rgba(66,133,244,0.1)', borderBottom: '1px solid rgba(66,133,244,0.2)', fontSize: 11, color: '#93C5FD', textAlign: 'center' }}>
            {syncMsg}
          </div>
        )}

        {/* ── Main layout: month + day panel ── */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

          {/* ── Month calendar ── */}
          <div style={{ flex: '0 0 auto', width: 'min(380px, 100%)', display: 'flex', flexDirection: 'column', borderRight: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>

            {/* Month navigation */}
            <div style={{ padding: '10px 14px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button onClick={prevMonth} style={navBtnStyle}>‹</button>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.9)', minWidth: 140, textAlign: 'center' }}>
                  {MONTH_NAMES[month]} {year}
                </span>
                <button onClick={nextMonth} style={navBtnStyle}>›</button>
              </div>
              <button onClick={goToday} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '4px 10px', color: 'rgba(255,255,255,0.65)', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Today</button>
            </div>

            {/* Day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '0 10px 4px' }}>
              {DAY_NAMES.map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 0' }}>{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '0 10px 10px', flex: 1, alignContent: 'start' }}>
              {Array.from({ length: totalCells }).map((_, i) => {
                const dayNum = i - firstDay + 1;
                const isCurrentMonth = dayNum >= 1 && dayNum <= daysInMonth;
                const cellDate = new Date(year, month, dayNum);
                const isToday = isCurrentMonth && isSameDay(cellDate, today);
                const isSelected = isCurrentMonth && isSameDay(cellDate, selectedDay);
                const key = `${cellDate.getFullYear()}-${cellDate.getMonth()}-${cellDate.getDate()}`;
                const dayEvs = eventsByDay[key] || [];

                return (
                  <div
                    key={i}
                    className={`cal-day${isToday ? ' cal-day-today' : ''}`}
                    onClick={() => isCurrentMonth && setSelectedDay(startOfDay(cellDate))}
                    style={{
                      minHeight: 46, borderRadius: 8, padding: '3px 2px',
                      cursor: isCurrentMonth ? 'pointer' : 'default',
                      background: isSelected ? `${GOLD}22` : 'transparent',
                      border: isSelected ? `1px solid ${GOLD}55` : '1px solid transparent',
                      transition: 'all 120ms', position: 'relative',
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                    }}
                  >
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isToday && !isSelected ? `${GOLD}` : 'transparent',
                      fontSize: 12, fontWeight: isToday ? 700 : 500,
                      color: isToday && !isSelected ? '#000' : isCurrentMonth ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.15)',
                      marginBottom: 2,
                    }}>
                      {isCurrentMonth ? dayNum : ''}
                    </div>
                    {/* Event dots */}
                    <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 36 }}>
                      {dayEvs.slice(0, 3).map((ev, idx) => (
                        <div key={idx} style={{ width: 5, height: 5, borderRadius: '50%', background: ev.color || GOLD, flexShrink: 0 }} />
                      ))}
                      {dayEvs.length > 3 && <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>+{dayEvs.length - 3}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Day panel ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            {/* Day header */}
            <div style={{ padding: '12px 16px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>
                  {selectedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
                <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{selectedDayEvents.length} event{selectedDayEvents.length !== 1 ? 's' : ''}</p>
              </div>
              <button
                onClick={() => { setShowModal(true); setEditingEvent(null); }}
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '5px 10px', color: 'rgba(255,255,255,0.65)', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
              >
                + Add
              </button>
            </div>

            {/* Day events list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {loading && <div style={{ textAlign: 'center', padding: 20, color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>Loading…</div>}
              {!loading && selectedDayEvents.length === 0 && (
                <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.25)', fontSize: 12 }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
                  No events for this day
                  <br />
                  <button
                    onClick={() => { setShowModal(true); setEditingEvent(null); }}
                    style={{ marginTop: 10, background: `${GOLD}22`, border: `1px solid ${GOLD}44`, color: GOLD, borderRadius: 8, padding: '6px 14px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                  >
                    + Create Event
                  </button>
                </div>
              )}
              {!loading && selectedDayEvents.map(ev => (
                <div
                  key={ev.id}
                  className="cal-ev-item"
                  onClick={() => { setEditingEvent(ev); setShowModal(true); }}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px',
                    borderRadius: 8, cursor: 'pointer', transition: 'background 120ms',
                    background: `${ev.color || GOLD}12`, border: `1px solid ${ev.color || GOLD}30`,
                  }}
                >
                  <div style={{ width: 3, borderRadius: 2, background: ev.color || GOLD, alignSelf: 'stretch', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.title}
                      {ev.google_event_id && <span style={{ marginLeft: 6, fontSize: 9, color: '#4285F4', fontWeight: 700, background: 'rgba(66,133,244,0.15)', padding: '1px 5px', borderRadius: 4 }}>G</span>}
                    </p>
                    {ev.all_day ? (
                      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>All day</p>
                    ) : (
                      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                        {new Date(ev.start_time).toLocaleTimeString('en-US', { timeZone: timezone, hour: 'numeric', minute: '2-digit', hour12: true })}
                        {' — '}
                        {new Date(ev.end_time).toLocaleTimeString('en-US', { timeZone: timezone, hour: 'numeric', minute: '2-digit', hour12: true })}
                      </p>
                    )}
                    {ev.location && <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📍 {ev.location}</p>}
                    {ev.description && <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ev.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Event Modal ── */}
      {showModal && (
        <div data-cal-modal>
          <EventModal
            event={editingEvent}
            defaultDate={selectedDay}
            timezone={timezone}
            googleConnected={googleStatus.connected}
            onSave={saveEvent}
            onDelete={deleteEvent}
            onClose={() => { setShowModal(false); setEditingEvent(null); }}
          />
        </div>
      )}
    </>
  );
}

const navBtnStyle = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: 'rgba(255,255,255,0.65)', fontSize: 16, fontWeight: 700,
};

// ─── TaskbarClock (exported) ──────────────────────────────────────────────────
export default function TaskbarClock({ defaultTimezone = 'America/Edmonton' }) {
  const [timezone, setTimezone] = useState(() => localStorage.getItem('qg-timezone') || defaultTimezone);
  const [clock, setClock]       = useState({ t: fmtTime(timezone), d: fmtDate(timezone) });
  const [open, setOpen]         = useState(false);

  // Tick every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => setClock({ t: fmtTime(timezone), d: fmtDate(timezone) }), 15000);
    setClock({ t: fmtTime(timezone), d: fmtDate(timezone) });
    return () => clearInterval(interval);
  }, [timezone]);

  const handleTimezoneChange = async (tz) => {
    setTimezone(tz);
    localStorage.setItem('qg-timezone', tz);
    setClock({ t: fmtTime(tz), d: fmtDate(tz) });
    authFetch('/api/calendar/timezone', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ timezone: tz }),
    }).catch(() => {});
  };

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          flexShrink: 0, padding: '4px 10px', textAlign: 'right', marginLeft: 2,
          background: open ? 'rgba(201,168,76,0.1)' : 'transparent',
          border: open ? `1px solid ${GOLD}40` : '1px solid transparent',
          borderRadius: 8, cursor: 'pointer', transition: 'all 150ms',
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'transparent'; }}
        title="Open Calendar"
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.8)', lineHeight: 1.3, whiteSpace: 'nowrap' }}>{clock.t}</div>
        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', lineHeight: 1.3, whiteSpace: 'nowrap' }}>{clock.d}</div>
        <div style={{ fontSize: 8, color: `${GOLD}99`, lineHeight: 1, marginTop: 1, whiteSpace: 'nowrap' }}>
          {ALL_ZONES.find(z => z.value === timezone)?.label?.match(/\(([^)]+)\)/)?.[1] || timezone.split('/')[1]?.replace('_', ' ') || 'TZ'}
        </div>
      </button>

      {open && (
        <CalendarPanel
          timezone={timezone}
          onTimezoneChange={handleTimezoneChange}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
